import { RadioButton } from "primereact/radiobutton";
import { Sidebar } from "primereact/sidebar";
import React, { useContext, useEffect, useRef } from "react";
import { LayoutContext } from "../../contexts/LayoutContext";
import { AppConfigProps } from "../../types";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { request } from "../../utils/Request";
import { Toast } from "primereact/toast";
import Auth from "../../utils/Auth";
import store from "store";

const AppConfig = (props: AppConfigProps) => {
    const {
        layoutConfig,
        setLayoutConfig,
        layoutState,
        setLayoutState,
    } = useContext(LayoutContext);
    const { t } = useTranslation();
    const scales = [12, 13, 14, 15, 16];
    const toast = useRef(null);
    const auth = new Auth();
    const user: any = auth.getUser();

    const toastify = (message: any, color: string = "info", summary: any = null) => {
        if (toast && toast.current) {
            // @ts-ignore
            toast.current.show({ severity: color, summary: summary, detail: message });
        }
    };

    const onSubmit = async () => {
        try {
            const response = await request({
                method: "patch",
                url: "auth/app/settings",
                data: {
                    app_settings: {
                        scale: layoutConfig.scale,
                        lang: layoutConfig.lang,
                    },
                    _method: "post",
                },
            });

            const { data } = response.data;

            if (data.app_settings) {
                store.set(
                    `${process.env.REACT_APP_STORAGE_PREFIX}.layoutConfig`,
                    data.app_settings
                );
                user.app_settings = data.app_settings;
                auth.setUser(user);
            }

            toastify(t("record updated", { item: t("app settings") }), "success");
            setTimeout(() => window.location.reload(), 1000);
        } catch (e: any) {
            // ignore
        }
    };

    const onConfigButtonClick = () => {
        setLayoutState((prevState) => ({
            ...prevState,
            configSidebarVisible: true,
        }));
    };

    const onConfigSidebarHide = () => {
        setLayoutState((prevState) => ({
            ...prevState,
            configSidebarVisible: false,
        }));
    };

    const decrementScale = () => {
        setLayoutConfig((prevState) => ({
            ...prevState,
            scale: prevState.scale - 1,
        }));
    };

    const incrementScale = () => {
        setLayoutConfig((prevState) => ({
            ...prevState,
            scale: prevState.scale + 1,
        }));
    };

    const applyScale = () => {
        document.documentElement.style.fontSize = layoutConfig.scale + "px";
    };

    const changeLanguage = (lang: string) => {
        setLayoutConfig((prevState) => ({ ...prevState, lang }));
    };

    useEffect(() => {
        applyScale();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutConfig.scale]);

    return (
        <>
            <button
                className="layout-config-button config-link"
                type="button"
                onClick={onConfigButtonClick}
            >
                <i className="pi pi-cog"></i>
            </button>

            <Sidebar
                visible={layoutState.configSidebarVisible}
                onHide={onConfigSidebarHide}
                position="right"
                className="layout-config-sidebar w-18rem"
            >
                <Toast ref={toast} />

                <h5 className="mt-1">{t("scales")}</h5>
                <div className="flex align-items-center">
                    <Button
                        icon="pi pi-minus"
                        type="button"
                        onClick={decrementScale}
                        className="w-2rem h-2rem mr-2"
                        rounded
                        text
                        disabled={layoutConfig.scale === scales[0]}
                    ></Button>
                    <div className="flex gap-2 align-items-center">
                        {scales.map((s, i) => (
                            <i
                                key={i}
                                className={classNames("pi pi-circle-fill text-300", {
                                    "text-primary-500": s === layoutConfig.scale,
                                })}
                            ></i>
                        ))}
                    </div>
                    <Button
                        icon="pi pi-plus"
                        type="button"
                        onClick={incrementScale}
                        className="w-2rem h-2rem ml-2"
                        rounded
                        text
                        disabled={layoutConfig.scale === scales[scales.length - 1]}
                    ></Button>
                </div>

                <h5>{t("language")}</h5>
                <div className="field-radiobutton">
                    <RadioButton
                        name="language"
                        value="eng"
                        checked={layoutConfig.lang === "eng"}
                        onChange={(e) => changeLanguage(e.value)}
                        inputId="lang-eng"
                    ></RadioButton>
                    <label htmlFor="lang-en">{t("english")}</label>
                </div>
                <div className="field-radiobutton">
                    <RadioButton
                        name="language"
                        value="gr"
                        checked={layoutConfig.lang === "gr"}
                        onChange={(e) => changeLanguage(e.value)}
                        inputId="lang-gr"
                    ></RadioButton>
                    <label htmlFor="lang-de">{t("german")}</label>
                </div>
                <div className="field-radiobutton">
                    <RadioButton
                        name="language"
                        value="it"
                        checked={layoutConfig.lang === "it"}
                        onChange={(e) => changeLanguage(e.value)}
                        inputId="lang-it"
                    ></RadioButton>
                    <label htmlFor="lang-it">{t("italian")}</label>
                </div>
                <div className="field-radiobutton">
                    <RadioButton
                        name="language"
                        value="sp"
                        checked={layoutConfig.lang === "sp"}
                        onChange={(e) => changeLanguage(e.value)}
                        inputId="lang-sp"
                    ></RadioButton>
                    <label htmlFor="lang-sp">{t("spanish")}</label>
                </div>
                <div className="field-radiobutton">
                    <RadioButton
                        name="language"
                        value="sl"
                        checked={layoutConfig.lang === "sl"}
                        onChange={(e) => changeLanguage(e.value)}
                        inputId="lang-sl"
                    ></RadioButton>
                    <label htmlFor="lang-sl">{t("slovenian")}</label>
                </div>

                <Button
                    onClick={() => onSubmit()}
                    tooltip={t("to data save into database")}
                    size="small"
                    outlined
                    tooltipOptions={{ position: "top" }}
                >
                    <i className="pi pi-save me-2" />
                    {t("let's save")}
                </Button>
            </Sidebar>
        </>
    );
};

export default AppConfig;
